
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { validateContactForm, sanitizeText, formatPhone } from "@/utils/validation";

export function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (field: string, value: string) => {
    let sanitizedValue = value;
    let fieldErrors = { ...errors };

    // Sanitize and validate based on field type
    switch (field) {
      case 'name':
        sanitizedValue = sanitizeText(value, 100);
        break;
      case 'email':
        sanitizedValue = sanitizeText(value, 255);
        break;
      case 'phone':
        sanitizedValue = formatPhone(sanitizeText(value, 20));
        break;
      case 'subject':
        sanitizedValue = sanitizeText(value, 200);
        break;
      case 'message':
        sanitizedValue = sanitizeText(value, 2000);
        break;
    }

    setFormData(prev => ({ ...prev, [field]: sanitizedValue }));
    
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      delete fieldErrors[field];
      setErrors(fieldErrors);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const validation = validateContactForm({
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      message: formData.message
    });

    if (!validation.isValid) {
      const errorMap: { [key: string]: string } = {};
      validation.errors.forEach(error => {
        if (error.includes('Nome')) errorMap.name = error;
        if (error.includes('Email')) errorMap.email = error;
        if (error.includes('Telefone')) errorMap.phone = error;
        if (error.includes('Mensagem')) errorMap.message = error;
      });
      setErrors(errorMap);
      return;
    }

    if (!formData.subject.trim()) {
      setErrors({ subject: 'Assunto é obrigatório' });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Here you would typically send the form data to your backend
      // For now, we'll simulate the submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: "Mensagem enviada!",
        description: "Entraremos em contato em breve.",
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setErrors({});
    } catch (error) {
      toast({
        title: "Erro ao enviar",
        description: "Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold font-primary text-gray-800 mb-6 text-center">
            Formulário de Contato
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name">Nome Completo *</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Seu nome completo"
                  required
                  maxLength={100}
                  className={errors.name ? "border-red-500" : ""}
                />
                {errors.name && <span className="text-red-500 text-sm">{errors.name}</span>}
              </div>
              <div>
                <Label htmlFor="email">E-mail *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="seu@email.com"
                  required
                  maxLength={255}
                  className={errors.email ? "border-red-500" : ""}
                />
                {errors.email && <span className="text-red-500 text-sm">{errors.email}</span>}
              </div>
            </div>
            
            <div>
              <Label htmlFor="phone">Telefone/WhatsApp</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="(11) 99999-9999"
                maxLength={20}
                className={errors.phone ? "border-red-500" : ""}
              />
              {errors.phone && <span className="text-red-500 text-sm">{errors.phone}</span>}
            </div>
            
            <div>
              <Label htmlFor="subject">Assunto *</Label>
              <select 
                id="subject"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary ${errors.subject ? "border-red-500" : "border-gray-300"}`}
                required
              >
                <option value="">Selecione um assunto</option>
                <option value="consulta">Agendar Consulta</option>
                <option value="informacoes">Solicitar Informações</option>
                <option value="parceria">Proposta de Parceria</option>
                <option value="outros">Outros</option>
              </select>
              {errors.subject && <span className="text-red-500 text-sm">{errors.subject}</span>}
            </div>
            
            <div>
              <Label htmlFor="message">Mensagem *</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                placeholder="Conte-me como posso ajudar você..."
                rows={5}
                required
                maxLength={2000}
                className={errors.message ? "border-red-500" : ""}
              />
              {errors.message && <span className="text-red-500 text-sm">{errors.message}</span>}
              <div className="text-xs text-gray-500 mt-1">
                {formData.message.length}/2000 caracteres
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-brand-primary hover:bg-brand-primary/90 font-accent"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Enviando..." : "Enviar Mensagem"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
